/**
 * 特集 - 囲みテキスト
 * 画像の四辺をテキストが周回する
 */

const ROOT_SEL = "[data-feature-rect-text]";
const READY_ATTR = "data-feature-rect-text-ready";
const SVG_NS = "http://www.w3.org/2000/svg";
const SPACER = "  ";

type Runtime = {
  resizeObserver: ResizeObserver;
};

const runtimes = new WeakMap<Element, Runtime>();

/**
 * 矩形パスの全長を埋めるまで文言を繰り返す
 */
const fillTextPath = (
  textPath: SVGTextPathElement,
  phrase: string,
  path: SVGPathElement,
): void => {
  const unit = `${phrase}${SPACER}`;
  textPath.textContent = unit;

  const textEl = textPath.parentElement;
  if (!(textEl instanceof SVGTextElement)) return;

  const unitLength = textEl.getComputedTextLength();
  const pathLength = path.getTotalLength();
  if (unitLength <= 0 || pathLength <= 0) return;

  const copies = Math.max(4, Math.ceil((pathLength * 1.5) / unitLength));
  textPath.textContent = unit.repeat(copies);
};

/**
 * startOffset の SMIL アニメーションを更新する
 */
const syncAnimation = (
  textPath: SVGTextPathElement,
  duration: number,
  reducedMotion: boolean,
): void => {
  const existing = textPath.querySelector("animate");
  if (existing) existing.remove();
  if (reducedMotion) {
    textPath.setAttribute("startOffset", "0%");
    return;
  }

  const animateEl = document.createElementNS(SVG_NS, "animate");
  animateEl.setAttribute("attributeName", "startOffset");
  animateEl.setAttribute("from", "0%");
  animateEl.setAttribute("to", "-100%");
  animateEl.setAttribute("dur", `${duration}s`);
  animateEl.setAttribute("repeatCount", "indefinite");
  textPath.appendChild(animateEl);
};

/**
 * SVG の矩形パスをフレームサイズに合わせて描き直す
 */
const layoutRoot = (root: Element): void => {
  const svg = root.querySelector("[data-feature-rect-text-svg]");
  const path = root.querySelector("[data-feature-rect-text-path]");
  const textPath = root.querySelector("[data-feature-rect-text-track]");
  if (
    !(svg instanceof SVGSVGElement) ||
    !(path instanceof SVGPathElement) ||
    !(textPath instanceof SVGTextPathElement)
  ) {
    return;
  }

  const width = Math.max(1, Math.round(svg.clientWidth));
  const height = Math.max(1, Math.round(svg.clientHeight));
  if (width < 2 || height < 2) return;

  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);

  const fontSize = Number(root.getAttribute("data-font-size")) || 14;
  const inset = Math.max(Math.round(fontSize * 0.75), 10);
  path.setAttribute(
    "d",
    `M ${inset},${inset} H ${width - inset} V ${height - inset} H ${inset} Z`,
  );

  const phrase = (root.getAttribute("data-marquee-text") || "").trim();
  if (phrase === "") {
    textPath.textContent = "";
    return;
  }

  fillTextPath(textPath, phrase, path);

  const duration = Number(root.getAttribute("data-duration")) || 40;
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  syncAnimation(textPath, duration, reducedMotion);
};

/**
 * 画像読み込みとリサイズ後にパスを合わせる
 */
const observeRoot = (root: Element): void => {
  const layout = () => {
    layoutRoot(root);
  };

  layout();

  const img = root.querySelector("img");
  if (img instanceof HTMLImageElement && !img.complete) {
    img.addEventListener("load", layout, { once: true });
  }

  if (document.fonts?.ready) {
    document.fonts.ready.then(layout).catch(() => {
      layout();
    });
  }

  const resizeObserver = new ResizeObserver(layout);
  resizeObserver.observe(root);
  runtimes.set(root, { resizeObserver });
};

/**
 * 未初期化の囲みテキストセクションを初期化する
 */
const initFeatureRectText = (): void => {
  const roots = document.querySelectorAll(ROOT_SEL);
  roots.forEach((root) => {
    if (root.getAttribute(READY_ATTR) === "true") return;
    root.setAttribute(READY_ATTR, "true");
    observeRoot(root);
  });
};

/**
 * 特集の囲みテキストアニメーションを初期化
 */
export function featureRectText(): void {
  initFeatureRectText();
  document.addEventListener("shopify:section:load", () => {
    initFeatureRectText();
  });
}
